import React, { useState, useEffect } from "react";
import TitledCard from "../../../../components/UI/TitledCard";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Divider from "@material-ui/core/Divider";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Input from "@material-ui/core/Input";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import PropTypes from "prop-types";
import { sortFeeSchedules, generateInternalFeeId, toUpperBounded, toLowerBounded } from "./helper";
import CurrencyNumber from "../../../../components/UI/CurrencyNumber";
import NumberFormat from "react-number-format";

function CurrencyFormatComponent(props) {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      allowNegative={false}
      thousandSeparator
      isNumericString
    />
  );
}

const internalIdName = "__TieredFeeDataBox__id";
const createInternalIds = (feeSchedules) => {
  const newFeeSchedules = [...feeSchedules];
  newFeeSchedules.forEach((feeSchedule) => {
    if (!feeSchedule[internalIdName]) {
      feeSchedule[internalIdName] = Math.random().toString().slice(2);
    }
  });
  return newFeeSchedules;
};

const TieredFeeDataBox = (props) => {
  const [breakpoint, setBreakpoint] = useState("");
  const [rate, setRate] = useState("");

  // I wish this language did function composition better
  const [feeSchedules, setFeeSchedules] = (
    useState(
      createInternalIds(
        sortFeeSchedules(
          toUpperBounded(props.feeSchedules || [])
        )
      )
    )
  );

  useEffect(() => {
    setFeeSchedules(
      createInternalIds(
        sortFeeSchedules(
          toUpperBounded(props.feeSchedules || [])
        )
      )
    );
  }, [props.feeSchedules]);

  const handleInput = (stateName, feeScheduleId) => (e) => {
    const value = Number(e.target.value);
    if (!feeScheduleId) {
      if (stateName === "rate") {
        setRate(value);
      }
      if (stateName === "breakpoint") {
        setBreakpoint(value);
      }
    } else {
      const newFeeSchedules = [...feeSchedules];
      newFeeSchedules.forEach((fee) => {
        if (fee[internalIdName] === feeScheduleId) {
          fee[stateName] = value;
        }
      });
      setFeeSchedules(sortFeeSchedules(newFeeSchedules));
    }
  };

  const addBreakPoint = () => {
    const newFeeSchedules = [...feeSchedules];
    const feeToAdd = [
      {
        [internalIdName]: generateInternalFeeId(),
        breakpoint: Number(breakpoint) === 0 ? null : breakpoint, // Use breakpoint: null is breakpoint is set to 0
        rate: rate,
      },
    ];
    // Add a tier of breakpoint: null is entered a non-0 breakpoint
    if (feeSchedules.length === 0 && breakpoint !== 0) {
      feeToAdd.push({
        breakpoint: null,
        rate: 0,
        [internalIdName]: generateInternalFeeId(),
      });
    }
    setRate("");
    setBreakpoint("");
    setFeeSchedules(sortFeeSchedules([...newFeeSchedules, ...feeToAdd]));
  };

  const handleRemoveFeeSchedule = (feeId) => (e) => {
    const newFeeSchedules = feeSchedules.filter(
      (f) => f[internalIdName] !== feeId
    );
    setFeeSchedules(
      newFeeSchedules.length === 1 ? [] : sortFeeSchedules(newFeeSchedules)
    ); // remove breakpoint: 0 tier if last breakpoint is removed)
  };

  // Props passed into this component to save fee schedules of this entity type
  const saveFeeSchedules = (e) => {
    if (props.onSaveFeeSchedules) {
      props.onSaveFeeSchedules(toLowerBounded(feeSchedules));
    }
  };

  const { readonly, title, description, hideDataBoxStyle } = props;

  // Add breakpoint is disabled when:
  // 1. breakpoint field or rate field is empty [OR]
  // 2. advisor has defined a 0-breakpoint to indicate a static fee schedule [OR]
  // 3. advisor has defined a non-0 breakpoint and try to add a 0-breakpoint, which contradicts the rule of fee schedules
  const disableAddBreakpointButton =
    breakpoint === "" ||
    rate === "" ||
    feeSchedules.some((fee) => fee.breakpoint === breakpoint) ||
    (Number(breakpoint) === 0 &&
      feeSchedules.some((fee) => fee.breakpoint !== 0));

  return (
    <TitledCard titleText="Fee Details" hideDataBoxStyle={hideDataBoxStyle}>
      {title}
      {description}
      {!readonly && (
        <Grid
          container
          spacing={3}
          alignItems={"center"}
          style={{ marginTop: 8 }}
        >
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel htmlFor="custodian-fee-breakpoint" shrink={true}>
                Breakpoint
              </InputLabel>
              <Input
                id="custodian-fee-breakpoint"
                value={breakpoint}
                onChange={handleInput("breakpoint")}
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
                inputComponent={CurrencyFormatComponent}
                data-cy="fees.tiered.add.breakpoint"
              />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel htmlFor="custodian-fee-rate" shrink={true}>
                Rate
              </InputLabel>
              <Input
                type="number"
                id="custodian-fee-rate"
                value={rate}
                onChange={handleInput("rate")}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                data-cy="fees.tiered.add.rate"
              />
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <Button
              variant="outlined"
              onClick={addBreakPoint}
              disabled={disableAddBreakpointButton}
              data-cy="fees.tiered.add.submit"
            >
              Add
            </Button>
          </Grid>
        </Grid>
      )}
      <Divider style={{ margin: "24px 0" }} />
      <Grid container spacing={3} alignItems={"center"}>
        {feeSchedules &&
          feeSchedules.length > 0 &&
          feeSchedules.map((fee, index) => {
            const isLastItem = index === feeSchedules.length - 1;
            const previousFee = feeSchedules[index - 1] || null;
            const feeId = fee[internalIdName] || fee.id;
            return (
              <React.Fragment key={feeId}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel
                      htmlFor={`custodian-fee-breakpoint-${feeId}`}
                      shrink={true}
                    >
                      {isLastItem ? (
                        "More than"
                      ) : previousFee ? (
                        <React.Fragment>
                          <CurrencyNumber value={previousFee.breakpoint} /> to
                        </React.Fragment>
                      ) : (
                        "Up to"
                      )}
                    </InputLabel>
                    <Input
                      data-cy="fees.tiered.breakpoints.first.breakpoint"
                      id={`custodian-fee-breakpoint-${feeId}`}
                      value={
                        isLastItem && previousFee
                          ? previousFee.breakpoint
                          : fee.breakpoint || 0
                      }
                      // to unbind the onChange event on the last item to prevent un-wanted trigger from the passive value change by CurrencyFormatComponent
                      onChange={
                        isLastItem
                          ? undefined
                          : handleInput("breakpoint", feeId)
                      }
                      startAdornment={
                        <InputAdornment position="start">$</InputAdornment>
                      }
                      disabled={isLastItem || readonly}
                      disableUnderline={isLastItem || readonly}
                      inputComponent={CurrencyFormatComponent}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel
                      htmlFor={`custodian-fee-rate-${feeId}`}
                      shrink={true}
                    />
                    <Input
                      data-cy="fees.tiered.breakpoints.first.rate"
                      type="number"
                      id={`custodian-fee-rate-${feeId}`}
                      value={fee.rate}
                      onChange={handleInput("rate", feeId)}
                      endAdornment={
                        <InputAdornment position="end">%</InputAdornment>
                      }
                      disabled={readonly}
                      disableUnderline={readonly}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  {!readonly && !isLastItem && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      size="small"
                      onClick={handleRemoveFeeSchedule(feeId)}
                    >
                      <DeleteIcon fontSize={"small"} />
                    </IconButton>
                  )}
                </Grid>
              </React.Fragment>
            );
          })}
      </Grid>
      {!readonly && (
        <Button
          variant="contained"
          color="primary"
          onClick={saveFeeSchedules}
          style={{ marginTop: 40 }}
          data-cy="fees.tiered.submit"
        >
          Save
        </Button>
      )}
    </TitledCard>
  );
};

TieredFeeDataBox.propTypes = {
  readonly: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onSaveFeeSchedules: PropTypes.func,
  hideDataBoxStyle: PropTypes.bool,
  feeSchedules: PropTypes.arrayOf(
    PropTypes.shape({
      breakpoint: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
};

TieredFeeDataBox.defaultProps = {
  readonly: false,
  hideDataBoxStyle: false,
};

export default TieredFeeDataBox;



// WEBPACK FOOTER //
// ./src/containers/Cy/components/TieredFeeDataBox/index.jsx